import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/Popover';
import type { EntityCrossFilterDimension } from '@/utils/entityCrossFilter';
import type { WorkspaceBulkActionOption } from '@/components/workspace/workspaceSelectionTypes';

export function WorkspaceTableSelectionActions({
  entityLabel,
  selectionCount,
  hasPendingChanges,
  canApply,
  onApply,
  onClearSelection,
  disabled = false,
  actions,
  onActionSelect,
  showApplyFilter = true,
}: {
  entityLabel: string;
  selectionCount: number;
  hasPendingChanges: boolean;
  canApply: boolean;
  onApply: () => void;
  onClearSelection: () => void;
  disabled?: boolean;
  actions?: WorkspaceBulkActionOption[];
  onActionSelect?: (actionId: string) => void;
  /** Workspace uses Apply to pin the selection onto other tables. */
  showApplyFilter?: boolean;
}) {
  const [actionsOpen, setActionsOpen] = useState(false);

  if (selectionCount === 0 && !hasPendingChanges) return null;

  const visibleActions = actions?.length ? actions : [];

  return (
    <div className="flex flex-wrap items-center gap-2.5 rounded-xl border border-selection-border bg-selection-bar px-5 py-2.5">
      <span className="text-body-md font-semibold text-brand-ink" role="status">
        {selectionCount} selected
        {hasPendingChanges ? <span className="text-warning-ink"> · unapplied</span> : null}
      </span>
      {selectionCount > 0 && visibleActions.length > 0 ? (
        <>
          <span className="h-4 w-px bg-selection-divider" aria-hidden />
          <Popover open={actionsOpen} onOpenChange={setActionsOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="secondary"
                size="sm"
                disabled={disabled}
                className="h-7 rounded-lg border-selection-divider text-brand-ink"
              >
                Actions
                <ChevronDown className="h-3.5 w-3.5" aria-hidden />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-64 rounded-xl border-border p-1.5 shadow-lg">
              <div className="flex flex-col">
                {visibleActions.map((action) => (
                  <button
                    key={action.id}
                    type="button"
                    disabled={disabled || action.disabled}
                    onClick={() => {
                      setActionsOpen(false);
                      onActionSelect?.(action.id);
                    }}
                    className="flex w-full flex-col items-start rounded-lg px-2.5 py-2 text-left text-body-md text-foreground hover:bg-surface-muted disabled:cursor-not-allowed disabled:text-foreground-muted disabled:hover:bg-transparent"
                    title={action.description}
                  >
                    <span>{action.label}</span>
                    {action.description ? (
                      <span className="text-body-sm text-foreground-muted">{action.description}</span>
                    ) : null}
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </>
      ) : null}
      {hasPendingChanges || showApplyFilter ? (
        <div className="ml-auto flex items-center gap-2">
          {hasPendingChanges ? (
            <Button variant="ghost" size="sm" onClick={onClearSelection} disabled={disabled}>
              Clear
            </Button>
          ) : null}
          {showApplyFilter ? (
            <Button
              size="sm"
              onClick={onApply}
              disabled={disabled || !canApply}
              aria-label={`Apply ${entityLabel} filter`}
            >
              Apply filter
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export const WORKSPACE_FILTER_DIMENSIONS = {
  portfolios: 'portfolioIds',
  campaigns: 'campaignKeys',
  adGroups: 'adGroupIds',
  keywords: 'keywordIds',
  adProducts: 'adProducts',
  targetingTypes: 'targetingTypes',
  matchTypes: 'matchTypes',
  placements: 'placements',
  audienceSegments: 'audienceSegments',
  searchTermKeys: 'searchTermKeys',
  asins: 'asins',
  adProductRowKeys: 'adProductRowKeys',
} as const satisfies Record<string, EntityCrossFilterDimension>;
